import React, { useEffect, useRef } from 'react';
import { Card, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { trackEvent } from '../utils/tracking';
import PLACEHOLDER_SVG from '../utils/imageFallback';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMapMarkerAlt,
    faStore,
    faLeaf,
    faWater,
    faGift,
    faGraduationCap,
    faStar,
    faPhone,
    faWarehouse,
    faShoppingBasket,
    faHandHoldingUsd,
    faTags,
    faClock
} from '@fortawesome/free-solid-svg-icons';

// Konstanten für wiederverwendbare Werte
const TRUNCATE_LENGTH = 140; // Länge für Beschreibungen
const MAX_VISIBLE_PRODUCTS = 3;

const ImkerCard = ({ location }) => {
    const {
        id,
        title,
        zip,
        city,
        country,
        locationType,
        openingHours,
        beekeeper,
        description,
        products,
        contact,
        images,
        closedPeriods,
        categories,
        state
    } = location;

    // Daten-Verarbeitungsfunktionen
    const formatAddress = () => `${zip} ${city}`;

    // Konvertiert den numerischen Typ in einen String
    const getTypeAsString = (typeNumber) => {
        // Konvertiere zu einer Zahl, falls es ein String ist
        const type = typeof typeNumber === 'string' ? parseInt(typeNumber, 10) : typeNumber;

        const types = {
            1: 'Lager',
            2: 'Hofladen',
            3: 'Verkaufsautomat',
            4: 'Selbstbedienung'
        };

        return types[type] || 'Unbekannt';
    };

    // Icon für den Typ
    const getTypeIcon = (typeNumber) => {
        // Konvertiere zu einer Zahl, falls es ein String ist
        const type = typeof typeNumber === 'string' ? parseInt(typeNumber, 10) : typeNumber;

        const icons = {
            1: faWarehouse,        // Lager
            2: faStore,            // Verkaufsladen
            3: faShoppingBasket,   // Verkaufsautomat
            4: faHandHoldingUsd    // Selbstbedienungsstelle
        };
        return icons[type] || faStore;
    };

    // Bild-Priorität: Location-Bilder > Profil-Bild > Placeholder
    const imageUrl = (images && images.length > 0 ? (images[0].url || images[0].thumbnail || images[0]) : null)
        || beekeeper?.profileImageUrl
        || PLACEHOLDER_SVG;

    // Überprüfen, ob ein Tag Öffnungszeiten hat
    const hasOpeningHoursForDay = (day) => {
        return openingHours && openingHours[day] && openingHours[day].trim() !== '';
    };

    // Den aktuellen Tag bekommen (z.B. "monday")
    const getCurrentDay = () => {
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        return days[new Date().getDay()];
    };

    // Überprüfen, ob heute geöffnet ist
    const isOpenToday = () => {
        const today = getCurrentDay();
        return hasOpeningHoursForDay(today);
    };

    // Öffnungszeiten für heute formatieren
    const getTodayHours = () => {
        const today = getCurrentDay();
        return openingHours?.[today] || 'Geschlossen';
    };

    // UI-Komponenten
    const CategoryIcon = ({ icon, color, text }) => (
        <Badge bg={color} text={color === "warning" || color === "light" ? "dark" : "white"} className="me-2 mb-2">
            <FontAwesomeIcon icon={icon} className="me-1" />
            {text}
        </Badge>
    );

    const CategoryIcons = () => {
        if (!beekeeper) return null;

        const categories = [
            { condition: beekeeper.isPremium, icon: faStar, color: "warning", text: "Premium" },
            { condition: beekeeper.isBio, icon: faLeaf, color: "success", text: "Bio" },
            { condition: beekeeper.hasLakeView, icon: faWater, color: "info", text: "Direkt am See" },
            { condition: beekeeper.hasShop, icon: faStore, color: "primary", text: "Hofladen" },
            { condition: beekeeper.hasSponsorship, icon: faGift, color: "secondary", text: "Bienenpatenschaft" },
            { condition: beekeeper.hasCourses, icon: faGraduationCap, color: "danger", text: "Kurse" }
        ];

        return (
            <div className="d-flex flex-wrap mb-3">
                {categories
                    .filter(category => category.condition)
                    .map((category, index) => (
                        <CategoryIcon
                            key={index}
                            icon={category.icon}
                            color={category.color}
                            text={category.text}
                        />
                    ))}
            </div>
        );
    };

    /* Rating disabled - no reviews in v1
    const Rating = () => {
        if (!beekeeper?.rating) return null;

        return (
            <div className="d-flex align-items-center">
                <div className="bg-warning-subtle text-warning rounded px-2 py-1">
                    <FontAwesomeIcon icon={faStar} className="me-1" />
                    <span className="fw-bold">{beekeeper.rating.toFixed(1)}</span>
                </div>
                {beekeeper.reviewCount && (
                    <small className="text-muted ms-2">({beekeeper.reviewCount})</small>
                )}
            </div>
        );
    };
    */

    const ProductBadges = () => {
        if (!products || products.length === 0) return null;

        // Nur die ersten MAX_VISIBLE_PRODUCTS anzeigen
        const visibleProducts = products.slice(0, MAX_VISIBLE_PRODUCTS);
        const additionalCount = products.length - MAX_VISIBLE_PRODUCTS;

        return (
            <div className="mb-3">
                <h6 className="mb-2 d-flex align-items-center">
                    <FontAwesomeIcon icon={faTags} className="me-2 text-primary" />
                    <span>Produkte</span>
                </h6>
                <div className="d-flex flex-wrap gap-2">
                    {visibleProducts.map((product, index) => (
                        <Badge 
                            key={product.id || index} 
                            bg="light" 
                            text="dark" 
                            className="border"
                            style={{ fontSize: '0.8rem' }}
                        >
                            {product.name || product.title}
                            {product.price && <span className="ms-1 fw-bold text-primary">{product.price}€</span>}
                        </Badge>
                    ))}
                    {additionalCount > 0 && (
                        <Badge 
                            bg="light" 
                            text="dark" 
                            className="border"
                            style={{ fontSize: '0.8rem' }}
                        >
                            +{additionalCount} weitere
                        </Badge>
                    )}
                </div>
            </div>
        );
    };

    const ContactInfo = () => {
        if (!contact?.phone) return null;

        return (
            <div className="d-flex align-items-center mb-3">
                <FontAwesomeIcon icon={faPhone} className="text-primary me-2" />
                <a href={`tel:${contact.phone}`} className="text-decoration-none small">
                    {contact.phone}
                </a>
            </div>
        );
    };

    // Geschlossene Zeiträume-Komponente
    const ClosedPeriods = () => {
        if (!closedPeriods || closedPeriods.length === 0) return null;

        return (
            <div className="mb-3 p-2 bg-danger-subtle rounded">
                <h6 className="text-danger mb-1 small fw-bold">Geschlossen:</h6>
                <ul className="list-unstyled small mb-0">
                    {closedPeriods.slice(0, 1).map((period, idx) => (
                        <li key={idx} className="text-danger">
                            {period.from} - {period.to}
                            {closedPeriods.length > 1 && <span className="ms-1">(+{closedPeriods.length - 1})</span>}
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const LocationDescription = () => {
        if (!description) return null;
        
        return (
            <div className="mb-3">
                <p className="text-muted small mb-0">
                    {description.length > TRUNCATE_LENGTH
                        ? `${description.substring(0, TRUNCATE_LENGTH)}...`
                        : description}
                </p>
            </div>
        );
    };

    const CategoryBadges = () => {
        if (!categories || categories.length === 0) return null;

        // Imkerei-thematische Farbpalette für die Kategorie-Badges
        const honeyColors = [
            { bg: "#F9A826", color: "#703600" },  // Honig-Gold mit dunklem Bernstein-Text
            { bg: "#E4B363", color: "#5C3D2E" },  // Bernstein mit Bienenwachs-Braun
            { bg: "#D68C45", color: "#FFFFFF" },  // Heller Honig mit weißem Text
            { bg: "#8E5E3F", color: "#F8EDD1" },  // Propolis-Braun mit cremefarbenem Text
            { bg: "#9C7A3B", color: "#FFFFFF" },  // Waben-Braun mit weißem Text
            { bg: "#A4AC86", color: "#3F4122" },  // Olivgrün mit dunkelgrünem Text (Bienenwachskerzen)
            { bg: "#B88746", color: "#FFFFFF" }   // Goldener Honig mit weißem Text
        ];

        return (
            <div className="d-flex flex-wrap gap-2 mb-3">
                {categories.slice(0, 3).map((category, index) => {
                    // Farben-Rotation für visuelle Vielfalt
                    const colorStyle = honeyColors[index % honeyColors.length];
                    
                    return (
                        <span 
                            key={category.id || index}
                            className="py-1 px-2 rounded-pill"
                            style={{
                                backgroundColor: colorStyle.bg,
                                color: colorStyle.color,
                                fontSize: "0.75rem",
                                fontWeight: "500",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.08)"
                            }}
                        >
                            {category.title}
                        </span>
                    );
                })}
                {categories.length > 3 && (
                    <span className="py-1 px-2 rounded-pill bg-light text-dark small">
                        +{categories.length - 3} weitere
                    </span>
                )}
            </div>
        );
    };

    const OpeningStatus = () => {
        if (!openingHours) return null;
        
        return (
            <div className="d-flex align-items-center mb-3">
                <FontAwesomeIcon 
                    icon={faClock} 
                    className={`me-2 ${isOpenToday() ? 'text-success' : 'text-muted'}`} 
                />
                <small className={isOpenToday() ? 'text-success' : 'text-muted'}>
                    {isOpenToday() ? `Heute: ${getTodayHours()}` : 'Heute geschlossen'}
                </small>
            </div>
        );
    };

    // IntersectionObserver for impression tracking (once per card)
    const cardRef = useRef(null);
    const impressionTracked = useRef(false);

    useEffect(() => {
        const node = cardRef.current;
        if (!node || impressionTracked.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !impressionTracked.current) {
                    impressionTracked.current = true;
                    trackEvent('impression', id);
                    observer.disconnect();
                }
            },
            { threshold: 0.5 }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [id]);

    return (
        <Card ref={cardRef} className="h-100 border-0 rounded-lg shadow-sm hover-card"
            style={{ overflow: 'hidden' }}>
            <div className="position-relative">
                {/* Bild mit Overlay-Effekt */}
                <div className="card-img-wrapper">
                    <Link to={`/imker-in/${country?.toLowerCase() || 'de'}/${state?.toLowerCase()}/${city?.toLowerCase()}/${id}`}>
                        <Card.Img
                            variant="top"
                            src={imageUrl}
                            alt={title || `Imkerei in ${formatAddress()}`}
                            className="img-fluid"
                            loading="lazy"
                            style={{
                                height: '220px',
                                objectFit: 'cover'
                            }}
                            onError={(e) => { e.target.src = PLACEHOLDER_SVG; }}
                        />
                    </Link>
                    <div className="img-overlay">
                        {/* Imker-Name im Overlay */}
                        {beekeeper && (
                            <div className="position-absolute bottom-0 start-0 p-3 text-white">
                                <div className="d-flex align-items-center">
                                    <h6 className="mb-0 text-white" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
                                        {beekeeper.firstName} {beekeeper.lastName}
                                    </h6>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Store-Typ Badge */}
                <div className="position-absolute top-0 end-0 m-2">
                    <Badge bg="warning" text="dark" className="px-2 py-1 rounded-pill">
                        <FontAwesomeIcon icon={getTypeIcon(locationType)} className="me-1" />
                        {getTypeAsString(locationType)}
                    </Badge>
                </div>
            </div>

            <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <h5 className="card-title fw-bold mb-0">{title}</h5>
                    {/* <Rating /> */}
                </div>

                <div className="d-flex align-items-center mb-3">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-danger mt-1 me-2" />
                    <p className="text-muted mb-0 small">{formatAddress()}</p>
                </div>

                <OpeningStatus />
                <CategoryIcons />
                <CategoryBadges />
                <LocationDescription />
                <ProductBadges />
                <ContactInfo />
                <ClosedPeriods />

                <div className="mt-auto pt-3">
                    <Link
                        to={`/imker-in/${country?.toLowerCase() || 'de'}/${state?.toLowerCase()}/${city?.toLowerCase()}/${id}`}
                        className="btn btn-primary w-100 rounded-pill"
                    >
                        Details ansehen
                    </Link>
                </div>
            </Card.Body>

        </Card>
    );
};

export default ImkerCard;